import { Button, Col, Divider, Input, Radio, Result, Row, Tooltip, Select, Checkbox, DatePicker, Switch } from 'antd';
import { CheckOutlined, CloseOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import moment from 'moment';
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";

import FormLayout from '../../FormLayout/FormLayout';
import TextField from '../../Fields/TextField/TextField';
import CountryField from '../../Fields/CountryField/CountryField';
import FundsSourceField from '../../Fields/FundsSourceField/FundsSourceField';

import { useLegalInvestorForm } from '../../../Hooks/useLegalInvestorForm/useLegalInvestorForm';
import { ContractAPI } from '../../../Api/ContractAPI';
import { InvestorAPI } from '../../../Api/InvestorAPI';

import { ILegalInvestorForm } from './ILegalInvestorForm';
import {
	AMOUNT_CANONICAL_OPTIONS,
	INCOME_CANONICAL_OPTIONS,
	LEGAL_SOURCE_CANONICAL_OPTIONS,
	SECTOR_CANONICAL_OPTIONS,
	getAmountOptions,
	getIncomeOptions,
	getLegalSourceOptions,
	getSectorOptions,
} from '../../../Assets/OptionsTranslations';

import './LegalInvestorForm.scss';

const { Option } = Select;

const LegalInvestorForm = ({
	baseURL,
	baseURLExternal,
	translations,
	clientId,
	unlockNextStep,
	language,
	isInvestorWithoutNIFEnabled,
	hideCommercialRegister = false,
	isReadOnly = false
}: ILegalInvestorForm) => {
	
	const contractAPI = ContractAPI(baseURL, baseURLExternal, clientId);
	const investorAPI = InvestorAPI(baseURL, baseURLExternal, clientId);

	const {
		placesService,
		placePredictions,
		getPlacePredictions,
		isPlacePredictionsLoading,
	} = usePlacesService({
		apiKey: "AIzaSyCU9qZlMZtXtxKvRJi-NRNJtPJF0p7DpYo",
	});

	const {
		loading,
		noContent,
		errors,
		investorData,
		nationalitiesList,
		activityInList,
		sourceInList,
		contractId,
		addressInputs,
		manualAddressVisibility,
		defaultValues,
		handleChangeData,
		handleSelectData,
		saveData,
		handleCheckedData,
		validateData,
		onChangeSameAddresses,
		handleSearchAddress,
		handleSelectAddress,
		onChangeNotificationsEnabled,
		contractLanguage,
	} = useLegalInvestorForm({
		translations,
		investorAPI,
		contractAPI,
		placesService,
		unlockNextStep,
		getPlacePredictions,
		hideCommercialRegister,
	});

	const sectorOptions = getSectorOptions(language);
	const legalSourceOptions = getLegalSourceOptions(language);
	const incomeOptions = getIncomeOptions(language);
	const amountOptions = getAmountOptions(language);

	return (
		<FormLayout 
			translations={translations}
			loading={loading}
			title={translations.formularioInversor}
			subTitle={translations.encabezadoFormulario}
			onClick={saveData}
			disabledSaveButton={noContent}
			contractId={contractId}
			nextStep={unlockNextStep}
			enableNextStep={validateData(false)}
			forceEnableNextStep={isReadOnly}
			hideSaveButton={isReadOnly}
		>	
			{!noContent
				? <>
					<Row gutter={[16, 16]} >
						<Col xs={24} md={12} lg={12} className="titles">
							<p>
								<label className="required"> {translations.nombreEntidad} </label>
								<Tooltip placement="topLeft" title={translations.tooltipformaLegal} >
									<InfoCircleOutlined className="tooltip" />
								</Tooltip>
								{errors.legalFormFormat
									? (
										<Tooltip placement="top" title={translations.entityNameTooltip} >
											<WarningOutlined className="tooltip" />
										</Tooltip>
									) : null
								}
							</p>
							<Input
								type="text"
								name="legalForm"
								value={investorData.legalForm}
								onChange={handleChangeData}
								disabled={isReadOnly}
								readOnly={isReadOnly}
								status={errors.legalForm || errors.legalFormFormat ? 'error' : ''}
							/>
							<span className={`error ${errors.legalForm ? 'show' : null}`}>{translations.completeFormaLegal}</span>
							<span className={`error ${errors.legalFormFormat ? 'show' : null}`}>{translations.invalidFormat}</span>
						</Col>

						<Col xs={24} md={12} lg={12} className="titles">
							<label className="required"> {translations.entityType}</label>
							<Select
								className="input-field"
								value={investorData.entityType}
								onChange={handleSelectData('entityType')}
								disabled={isReadOnly}
								status={errors.entityType ? 'error' : ''}
								placeholder={translations.seleccioneOpcion}
								showSearch
								filterOption={(input, option) =>
									(option!.children as unknown as string).toString().toLowerCase().includes(input.toLowerCase())
								}
							>
								<Option value="corporation">{translations.incorporatedCompany}</Option>
								<Option value="limited">{translations.limitedLiabilityCompany}</Option>
								<Option value="sole">{translations.soleProprietorshipCompany}</Option>
								<Option value="nonprofit">{translations.nonProfitCompany}</Option>
								<Option value="ngo">{translations.nonGovernmentalOrganizations}</Option>
								<Option value="associations">{translations.associations}</Option>
								<Option value="religious">{translations.religiousOrder}</Option>
								<Option value="ventureCapital">{translations.ventureCapital}</Option>
								<Option value="foundation">{translations.foundation}</Option>
								<Option value="pensionFund">{translations.pensionFund}</Option>
								<Option value="mutuality">{translations.mutuality}</Option>
							</Select>
							<span className={`error ${errors.entityType ? 'show' : null}`}>{translations.completeEntityType}</span>
						</Col>

						<Col xs={24} md={12} lg={12} className="titles">
							<label>{translations.nombreGestor}</label>
							<Input
								type="text"
								name="managerName"
								value={investorData.managerName}
								disabled={isReadOnly}
								readOnly={isReadOnly}
								onChange={handleChangeData}
							/>
						</Col>
						<Col xs={24} md={12} lg={12} className="titles">
							<label className="required"> {translations.fechaConstitucion}</label>
							<DatePicker
								allowClear={false}
								className="input-field"
								name="docValidThru"
								value={investorData.constitutionDate ? moment(investorData.constitutionDate) : undefined}
								format="DD/MM/YYYY"
								onChange={(e) => handleChangeData({ target: { name: 'constitutionDate', value: e?.format('YYYY-MM-DD')} })}
								disabled={isReadOnly}
								inputReadOnly={isReadOnly}
								status={errors.docValidThru ? 'error' : ''}
							/>
							<span className={`error ${errors.constitutionDate ? 'show' : null}`}>{translations.completeCampo}</span>
						</Col>
						<Col xs={24} md={12} lg={12} className="titles">
							<label className="required"> {translations.lugarConstitucion}</label>
							<Input
								type="text"
								name="constitutionPlace"
								value={investorData.constitutionPlace}
								disabled={isReadOnly}
								readOnly={isReadOnly}
								onChange={handleChangeData}
								status={errors.constitutionPlace ? 'error' : ''}
							/>
							<span className={`error ${errors.constitutionPlace ? 'show' : null}`}>{translations.completeCampo}</span>
						</Col>
						<Col xs={24} md={12} lg={12} className="titles">
							<label className="required"> {translations.jurisdiccion}</label>
							<Input
								type="text"
								name="jurisdiction"
								value={investorData.jurisdiction}
								disabled={isReadOnly}
								readOnly={isReadOnly}
								onChange={handleChangeData}
								status={errors.jurisdiction ? 'error' : ''}
							/>
							<span className={`error ${errors.jurisdiction ? 'show' : null}`}>{translations.completeCampo}</span>
						</Col>
						<Col xs={24} md={12} lg={12} className="titles">
							<label>{translations.leiCode}</label>
							<Input
								type="text"
								name="lei"
								value={investorData.lei}
								disabled={isReadOnly}
								readOnly={isReadOnly}
								onChange={handleChangeData}
								status={errors.lei ? 'error' : ''}
							/>
						</Col>
						<Col xs={24} md={12} lg={12} className="titles">
							<label>{translations.paginaWeb}</label>
							<Input
								type="text"
								name="webPage"
								value={investorData.webPage}
								disabled={isReadOnly}
								readOnly={isReadOnly}
								onChange={handleChangeData}
							/>
						</Col>
						{!hideCommercialRegister
							? <Col xs={24} md={12} lg={12} className="titles">
								<label className="required"> {translations.registroMercantil}</label>
								<Input
									type="text"
									name="commercialRegister"
									value={investorData.commercialRegister}
									disabled={isReadOnly}
									readOnly={isReadOnly}
									onChange={handleChangeData}
									status={errors.commercialRegister ? 'error' : ''}
								/>
								<span className={`error ${errors.commercialRegister ? 'show' : null}`}>{translations.completeCampo}</span>
							</Col>
							: null
						}
						<CountryField
							value={investorData.entityNationality}
							fieldName="entityNationality"
							nationalitiesList={nationalitiesList}
							translations={translations}
							errors={[errors]}
							index={0}
							handleSelect={handleSelectData}
							language={language}
							contractLanguage={language}
							disabled={isReadOnly}
						/>
						<Col xs={24} md={6} lg={6} className="titles">
							<label className="required"> {translations.cifLabel || 'CIF'} </label>
							<Input
								type="text"
								name="cif"
								value={investorData.cif}
								disabled={isReadOnly}
								readOnly={isReadOnly}
								onChange={handleChangeData}
								status={errors.cif ? 'error' : ''}
							/>
							<span className={`error ${errors.cif ? 'show' : null}`}>{translations.completeCampo}</span>
						</Col>
						<Col xs={24} md={12} lg={12} className="titles">
							<p>
								<label>{translations.taxIdentification} </label>
								<Tooltip placement="topLeft" title={translations.tooltipIdentificacionFiscal} >
									<InfoCircleOutlined className="tooltip" />
								</Tooltip>
							</p>
							<Input
								type="text"
								name="taxIdentification"
								value={investorData.taxIdentification}
								disabled={isReadOnly}
								readOnly={isReadOnly}
								onChange={handleChangeData}
							/>
						</Col>
					</Row>
					<Row gutter={[16, 16]}>
						<Col xs={24} md={12} lg={12}>
							<label className="required"> {translations.socialAddress} </label>
							<Select
								style={{ width: "100%" }}
								showSearch
								value={addressInputs[0]}
								placeholder={translations.direccion}
								disabled={isReadOnly}
								open={isReadOnly ? false : undefined}
								onSearch={handleSearchAddress('socialAddress')}
								onChange={handleSelectAddress('socialAddress')}
								filterOption={false} // Importante: para que solo muestre las opciones de Google
								notFoundContent={isPlacePredictionsLoading ? "Loading..." : null}
							>
								{placePredictions.map((item) => (
									<Option key={item.place_id} value={item.description}>
										{item.description}
									</Option>
								))}
							</Select>
							<span className={`error ${errors.socialAddress ? 'show' : null}`}>{translations.completeCampo}</span>
						</Col>
						<TextField
							size='m'
							value={investorData.socialAddressExtra}
							fieldName="socialAddressExtra"
							placeholder={translations.socialAddressExtra}
							translations={translations}
							required={false}
							errors={errors}
							handleChange={(index) => handleChangeData}
							disabled={isReadOnly}
						/>
						{manualAddressVisibility.socialAddress && (
							<>
								<TextField
									size='m'
									label={translations.direccion}
									value={investorData.socialAddress}
									fieldName="socialAddress"
									errorWhenEmpty
									translations={translations}
									required
									errors={errors}
									handleChange={(index) => handleChangeData}
								/>
								<TextField
									size='s'
									value={investorData.socialAddressPostalCode}
									fieldName="socialAddressPostalCode"
									translations={translations}
									required
									errors={errors}
									handleChange={(index) => handleChangeData}
								/>
								<TextField
									size='s'
									value={investorData.socialAddressCity}
									fieldName="socialAddressCity"
									translations={translations}
									required
									errors={errors}
									handleChange={(index) => handleChangeData}
								/>
								<CountryField
									value={investorData.socialAddressCountry}
									fieldName="socialAddressCountry"
									nationalitiesList={nationalitiesList}
									translations={translations}
									errors={[errors]}
									index={0}
									handleSelect={handleSelectData}
									language={language}
									contractLanguage={contractLanguage}
								/>
							</>
						)}
						<Col span="24">
							<label> {translations.taxResidenceEqualsSocialAddress}     </label>
							<Switch
								checked={investorData.sameAddresses}
								checkedChildren={<CheckOutlined />}
								unCheckedChildren={<CloseOutlined />}
								disabled={isReadOnly}
								onChange={onChangeSameAddresses}
							/>
						</Col>
						{!investorData.sameAddresses
							? <>
								<Col xs={24} md={12} lg={12}>
									<label className="required"> {translations.taxResidence} </label>
									<Select
										style={{ width: "100%" }}
										showSearch
										value={addressInputs[1]}
										placeholder={translations.direccion}
										disabled={isReadOnly}
										open={isReadOnly ? false : undefined}
										onSearch={handleSearchAddress('taxResidence')}
										onChange={handleSelectAddress('taxResidence')}
										filterOption={false} // Importante: para que solo muestre las opciones de Google
										notFoundContent={isPlacePredictionsLoading ? "Cargando..." : null}
									>
										{placePredictions.map((item) => (
											<Option key={item.place_id} value={item.description}>
												{item.description}
											</Option>
										))}
									</Select>
									<span className={`error ${errors.taxResidence ? 'show' : null}`}>{translations.completeCampo}</span>
								</Col>
								<TextField
									size='m'
									value={investorData.taxResidenceExtra}
									fieldName="taxResidenceExtra"
									placeholder={translations.taxResidenceExtra}
									translations={translations}
									required={false}
									errors={errors}
									handleChange={(index) => handleChangeData}
									disabled={isReadOnly}
								/>
								{manualAddressVisibility.taxResidence && (
									<>
										<TextField
											size='m'
											label={translations.direccion}
											value={investorData.taxResidence}
											fieldName="taxResidence"
											errorWhenEmpty
											translations={translations}
											required
											errors={errors}
											handleChange={(index) => handleChangeData}
										/>
										<TextField
											size='s'
											value={investorData.taxResidencePostalCode}
											fieldName="taxResidencePostalCode"
											translations={translations}
											required
											errors={errors}
											handleChange={(index) => handleChangeData}
										/>
										<TextField
											size='s'
											value={investorData.taxResidenceCity}
											fieldName="taxResidenceCity"
											translations={translations}
											required
											errors={errors}
											handleChange={(index) => handleChangeData}
										/>
										<CountryField
											value={investorData.countryTaxResidence}
											fieldName="countryTaxResidence"
											nationalitiesList={nationalitiesList}
											translations={translations}
											errors={[errors]}
											index={0}
											handleSelect={handleSelectData}
											language={language}
											contractLanguage={contractLanguage}
										/>
									</>
								)}
							</> : null
						}
						{isInvestorWithoutNIFEnabled ?
							<Col xs={24} md={24} lg={24} className="titles">
								<label>{translations.encabezadoRazon}</label>
								<Row className="nif-radio-group">
									<Radio.Group
										name="notNIFReason"
										value={investorData.notNIFReason}
										disabled={isReadOnly}
										onChange={handleChangeData}
									>
										<Radio style={{ whiteSpace: 'normal' }} value={1}>{translations.razonA}</Radio> <br></br>
										<Radio style={{ whiteSpace: 'normal' }} value={2}>{translations.razonB}</Radio>
										{/*  Este input, sólo se habilita si se selecciona la Razón B */}
										<Input
											placeholder={translations.placeHolderRazon}
											disabled={isReadOnly || investorData.notNIFReason !== 2}
											readOnly={isReadOnly}
											name="notNIFDetailReason"
											value={investorData.notNIFDetailReason}
											onChange={handleChangeData}
										/>
										<Radio style={{ whiteSpace: 'normal' }} value={3}>{translations.razonC}</Radio>
									</Radio.Group>
								</Row>
							</Col>
							: null
						}
						<Divider />
					</Row>
					<Row gutter={[16, 16]} align="bottom">
						{!activityInList ? 
							<>
								<Col xs={24} md={6} lg={6} className="titles">
									<label className="required"> {translations.sectorFirmante} </label>
									<Select
										className="input-field"
										value={investorData.sector}
										onChange={handleSelectData('sector')}
										disabled={isReadOnly}
										status={errors.sector ? 'error' : ''}
										placeholder={translations.seleccioneOpcion}
										showSearch
										filterOption={(input, option) =>
											(option!.children as unknown as string).includes(input)
										}
									>
										{sectorOptions.map((option: string, idx: number) => (
											<Option key={idx} value={SECTOR_CANONICAL_OPTIONS[idx] || option}>{option}</Option>
										))}
										<Option value="Otro">{translations.otherSpecify}</Option>
									</Select>
									<span className={`error ${errors.sector ? 'show' : null}`}>{translations.seleccioneOpcion}</span>
								</Col>
								<Col xs={24} md={6} lg={6} className="titles">
									<label className="required">{translations.otherSector}</label>
									<Input 
										name="otherSector"
										value={investorData.otherSector}
										disabled={isReadOnly}
										readOnly={isReadOnly}
										onChange={handleChangeData}
										/>
								</Col>							
							</>
							:
							<>
								<Col xs={24} md={12} lg={12} className="titles">
									<label className="required"> {translations.sectorFirmante} </label>
									<Select
										className="input-field"
										value={investorData.sector}
										onChange={handleSelectData('sector')}
										disabled={isReadOnly}
										status={errors.sector ? 'error' : ''}
										placeholder={translations.seleccioneOpcion}
									>
										{sectorOptions.map((option: string, idx: number) => (
											<Option key={idx} value={SECTOR_CANONICAL_OPTIONS[idx] || option}>{option}</Option>
										))}
										<Option value="Otro">{translations.otherSpecify}</Option>
									</Select>
									<span className={`error ${errors.sector ? 'show' : null}`}>{translations.seleccioneOpcion}</span>
								</Col>
							</>
						}
						<>
							<FundsSourceField
								value={ investorData.source }
								fieldName='source'
								index={ 0 }
								translations={ {...translations, sourceOptions: legalSourceOptions } }
								canonicalValues={LEGAL_SOURCE_CANONICAL_OPTIONS}
								errors={ [errors] }
								handleSelectOrDatePicker={ handleSelectData }
								disabled={isReadOnly}
							/>
							{
								!sourceInList 
									? <TextField
										size='m'
										value={ investorData.otherSource }
										fieldName="otherSource"
										placeholder={ translations.otherSource }
										translations={ translations }
										required={ false }
										errors={ [errors] }
										index={ 0 }
										handleChange={ (index) => handleChangeData }
										disabled={isReadOnly}
									/>
									: null
							}
						</>
						<Col xs={24} md={12} lg={12} className="titles">
							<label className="required"> {translations.anualIncome}</label>
							<Select
								className="input-field"
								value={investorData.incomes}
								onChange={handleSelectData('incomes')}
								disabled={isReadOnly}
								status={errors.incomes ? 'error' : ''}
								placeholder={translations.seleccioneOpcion}
							>
								{incomeOptions.map((option: string, idx: number) => (
									<Option key={idx} value={INCOME_CANONICAL_OPTIONS[idx] || option}>{option}</Option>
								))}
							</Select>
							<span className={`error ${errors.incomes ? 'show' : null}`}>{translations.seleccioneOpcion}</span>
						</Col>
						<Col xs={24} md={12} lg={12} className="titles">
							<label className="required"> {translations.netWorth}</label>
							<Select
								className="input-field"
								value={investorData.lastPatrimony}
								onChange={handleSelectData('lastPatrimony')}
								disabled={isReadOnly}
								status={errors.lastPatrimony ? 'error' : ''}
								placeholder={translations.seleccioneOpcion}
							>
								{amountOptions.map((option: string, idx: number) => (
									<Option key={idx} value={AMOUNT_CANONICAL_OPTIONS[idx] || option}>{option}</Option>
								))}
							</Select>
							<span className={`error ${errors.lastPatrimony ? 'show' : null}`}>{translations.seleccioneOpcion}</span>
						</Col>
						<Col xs={24} md={12} lg={12} className="titles">
							<label className="required"> {translations.territoriosOpera}</label>
							<Input
								type="text"
								name="operationCountries"
								value={investorData.operationCountries}
								disabled={isReadOnly}
								readOnly={isReadOnly}
								onChange={handleChangeData}
								status={errors.operationCountries ? 'error' : ''}
							/>
							<span className={`error ${errors.operationCountries ? 'show' : null}`}>{translations.completeCampo}</span>
						</Col>
					</Row>
					<Divider />
					<Row gutter={[16, 16]}>
						<Col xs={24} md={12} lg={12} className="titles">
							<p>
								<label className="required"> {translations.bancoDepositos} </label>
								<Tooltip placement="topLeft" title={translations.tooltipbanco} >
									<InfoCircleOutlined className="tooltip" />
								</Tooltip>
							</p>
							<Input
								type="text"
								name="bankDeposits"
								value={investorData.bankDeposits}
								onChange={handleChangeData}
								status={errors.bankDeposits ? 'error' : ''}
								disabled={isReadOnly || defaultValues.some(defVal => defVal.field === "bankDeposits" && defVal.value)}
								readOnly={isReadOnly}
							/>
							<span className={`error ${errors.bankDeposits ? 'show' : null}`}>{translations.completeCampo}</span>
						</Col>
						<Col xs={24} md={12} lg={12} className="titles">
							<label className="required"> {translations.bankAddress}</label>
							<Input
								type="text"
								name="bankAddress"
								value={investorData.bankAddress}
								onChange={handleChangeData}
								status={errors.bankAddress ? 'error' : ''}
								disabled={isReadOnly || defaultValues.some(defVal => defVal.field === "bankAddress" && defVal.value)}
								readOnly={isReadOnly}
							/>
							<span className={`error ${errors.bankAddress ? 'show' : null}`}>{translations.completeCampo}</span>
						</Col>
						<Col xs={24} md={12} lg={12} className="titles">
							<label className="required"> {translations.iban}</label>
							<Input
								type="text"
								name="iban"
								value={investorData.iban}
								onChange={handleChangeData}
								status={errors.iban ? 'error' : ''}
								disabled={isReadOnly || defaultValues.some(defVal => defVal.field === "iban" && defVal.value)}
								readOnly={isReadOnly}
							/>
							<span className={`error ${errors.iban ? 'show' : null}`}>{translations.completeCampo}</span>
						</Col>
						<Col xs={24} md={12} lg={12} className="titles">
							<label className="required"> {translations.codigoBic}</label>
							<Input
								type="text"
								name="biC_SWIFT"
								value={investorData.biC_SWIFT}
								onChange={handleChangeData}
								status={errors.biC_SWIFT ? 'error' : ''}
								disabled={isReadOnly || defaultValues.some(defVal => defVal.field === "biC_SWIFT" && defVal.value)}
								readOnly={isReadOnly}
							/>
							<span className={`error ${errors.biC_SWIFT ? 'show' : null}`}>{translations.completeCampo}</span>
						</Col>
					</Row>
					<Divider />
					<Row>
						<Checkbox
							className='qualitas-checkbox'
							name="organism"
							checked={investorData.organism}
							value= {investorData.organism}
							disabled={isReadOnly}
							onChange={handleCheckedData}
						>
							<label>{translations.encabezadoOcde}</label>
						</Checkbox>
					</Row>

					{/* Los 4 campos siguientes sólo se muestrar si se selecciona SI */}
					{investorData.organism
						? <Row gutter={[16, 16]}>
							<Col xs={24} md={24} lg={24} className="titles">
								{translations.encabezadoSiOcde}
							</Col>
							<Col xs={24} md={12} lg={12} className="titles">
								<label className="required"> {translations.nombreAutoridad}</label>
								<Input
									type="text"
									name="regulationOrganism"
									value={investorData.regulationOrganism}
									disabled={isReadOnly}
									readOnly={isReadOnly}
									onChange={handleChangeData}
									status={errors.regulationOrganism ? 'error' : ''}
								/>
								<span className={`error ${errors.regulationOrganism ? 'show' : null}`}>{translations.completeCampo}</span>
							</Col>
							<Col xs={24} md={12} lg={12} className="titles">
								<label className="required"> {translations.paisAutoridad}</label>
								<Input
									type="text"
									name="countryRegulator"
									value={investorData.countryRegulator}
									disabled={isReadOnly}
									readOnly={isReadOnly}
									onChange={handleChangeData}
									status={errors.countryRegulator ? 'error' : ''}
								/>
								<span className={`error ${errors.countryRegulator ? 'show' : null}`}>{translations.completeCampo}</span>
							</Col>
							<Col xs={24} md={12} lg={12} className="titles">
								<label className="required"> {translations.paginaWebSi}</label>
								<Input
									type="text"
									name="webRegulator"
									value={investorData.webRegulator}
									disabled={isReadOnly}
									readOnly={isReadOnly}
									onChange={handleChangeData}
									status={errors.webRegulator ? 'error' : ''}
								/>
								<span className={`error ${errors.webRegulator ? 'show' : null}`}>{translations.completeCampo}</span>
							</Col>
							<Col xs={24} md={12} lg={12} className="titles">
								<label className="required"> {translations.numeroRegistroSi}</label>
								<Input
									type="text"
									name="regulatoryRecord"
									value={investorData.regulatoryRecord}
									disabled={isReadOnly}
									readOnly={isReadOnly}
									onChange={handleChangeData}
									status={errors.regulatoryRecord ? 'error' : ''}
								/>
								<span className={`error ${errors.regulatoryRecord ? 'show' : null}`}>{translations.completeCampo}</span>
							</Col>
						</Row>
						: null
					}
					<Divider />
					<Row>
						<Checkbox
							className='qualitas-checkbox'
							name="contributor"
							checked={investorData.contributor}
							value= {investorData.contributor}
							disabled={isReadOnly}
							onChange={handleCheckedData}
						>
							<label>{translations.encabezadoCotiza}</label>
						</Checkbox>
					</Row>

					{/* Los 4 campos siguientes sólo se muestrar si se selecciona SI */}
					{investorData.contributor
						? <Row gutter={[16, 16]}>
							<Col xs={24} md={24} lg={24} className="titles">
								{translations.encabezadoSiOcde}
							</Col>
							<Col xs={24} md={12} lg={12} className="titles">
								<label className="required"> {translations.nombreAutoridad}</label>
								<Input
									type="text"
									name="marketName"
									value={investorData.marketName}
									disabled={isReadOnly}
									readOnly={isReadOnly}
									onChange={handleChangeData}
									status={errors.marketName ? 'error' : ''}
								/>
								<span className={`error ${errors.marketName ? 'show' : null}`}>{translations.completeCampo}</span>
							</Col>
							<Col xs={24} md={12} lg={12} className="titles">
								<label className="required"> {translations.paisAutoridad}</label>
								<Input
									type="text"
									name="marketCountry"
									value={investorData.marketCountry}
									disabled={isReadOnly}
									readOnly={isReadOnly}
									onChange={handleChangeData}
									status={errors.marketCountry ? 'error' : ''}
								/>
								<span className={`error ${errors.marketCountry ? 'show' : null}`}>{translations.completeCampo}</span>
							</Col>
							<Col xs={24} md={12} lg={12} className="titles">
								<label className="required"> {translations.paginaWebSi}</label>
								<Input
									type="text"
									name="marketWeb"
									value={investorData.marketWeb}
									disabled={isReadOnly}
									readOnly={isReadOnly}
									onChange={handleChangeData}
									status={errors.marketWeb ? 'error' : ''}
								/>
								<span className={`error ${errors.marketWeb ? 'show' : null}`}>{translations.completeCampo}</span>
							</Col>
							<Col xs={24} md={12} lg={12} className="titles">
								<label className="required"> {translations.numeroRegistroSi}</label>
								<Input
									type="text"
									name="marketRegistry"
									value={investorData.marketRegistry}
									disabled={isReadOnly}
									readOnly={isReadOnly}
									onChange={handleChangeData}
									status={errors.marketRegistry ? 'error' : ''}
								/>
								<span className={`error ${errors.marketRegistry ? 'show' : null}`}>{translations.completeCampo}</span>
							</Col>
						</Row>
						: null
					}
				</>
				:
				<div className="no-content-wrapper">
					<Result
						status="warning"
						title={translations.messageContrato}
						extra={
							<Button type="primary" key="console">
								{translations.inicio}
							</Button>
						}
					/>
				</div>
			}
		</FormLayout>
	);
};

export default LegalInvestorForm;
